import { FullScreenDialogTransparent } from "designSystem/components/dialog/FullScreenDialog";
import { useSyncLocalStore } from "designSystem/hooks";
import { ImageData } from "csi/model/image";
import React, { FunctionComponent } from "react";
import { BackgroundColorHandlerFunction } from "designSystem/components/colorPicker/ColorPicker";
import LightboxStore, { LightboxChangeFunction, LightboxEditFunction, LightboxStoreContext } from "./LightboxStore";
import PrivateLightbox from "./PrivateLightbox";

export interface LightboxProps {
	/** lightbox's background color (defaults to #FFFFFF) */
	backgroundColor?: string;

	/** list of images to be displayed */
	images: ImageData[];

	/** callback to handle when a change to the background color happens */
	onBackgroundColorChange?: BackgroundColorHandlerFunction;

	/** callback to handle when lightbox image attribute(s) has been updated */
	onChange?: LightboxChangeFunction;

	/** callback to handle when lightbox dialog is closed */
	onClose?: VoidFunction;

	/** callback to handle when lightbox edit button is clicked */
	onEdit?: LightboxEditFunction;

	/** list of custom background colors (up to 9) */
	recentCustomColors?: string[];

	/** index of image to show initially (defaults to 0) */
	startIndex?: number;

	/** z-index of dialog */
	zIndex?: number;
}

const Lightbox: FunctionComponent<LightboxProps> = (props) => {
	const {
		backgroundColor = "#FFFFFF",
		images,
		onBackgroundColorChange,
		onChange,
		onClose,
		onEdit,
		recentCustomColors,
		startIndex = 0,
		zIndex,
	} = props;

	if (!images.length) {
		return null;
	}

	const store = useSyncLocalStore(() => new LightboxStore(
		images,
		onBackgroundColorChange,
		onChange,
		onClose,
		onEdit,
		startIndex,
		backgroundColor,
		recentCustomColors,
	));

	return (
		<LightboxStoreContext.Provider value={store}>
			<FullScreenDialogTransparent
				// when trying to edit the image and perform a "rotate left" operation in the Lightbox
				// user is getting "Uncaught RangeError: Maximum call stack size exceeded." in the DevTools console
				// the cause is that Dojo image editor appearing above the lightbox and MUI is disabling the event of Dojo widget
				// to prevent this from happening, pass disableEnforceFocus to MUI Dialog component
				disableEnforceFocus
				onClose={onClose}
				open
				sx={zIndex ? { zIndex } : null}
			>
				<PrivateLightbox />
			</FullScreenDialogTransparent>
		</LightboxStoreContext.Provider>
	);
};

export default Lightbox;
